import PropTypes from "prop-types";
import * as React from "react";
import { Link as RouterLink } from "react-router-dom";
import { ExternalLinkIcon } from "../../primitives/Icons/Icons";
import shared from "../../shared.module.css";
import { colorOf } from "../../utils/propTypes";
import css from "./Link.module.css";

const PROTOCOL_RE = /^[a-z][a-z\d+.-]*:/i;

const HANDOFF_RE = /^(mailto|tel):/i;

const isExternalUrl = (url) => PROTOCOL_RE.test(url);

/* A new tab for mailto:/tel: leaves a blank tab behind, and some mail clients fail to open. */
const isHandoffUrl = (url) => HANDOFF_RE.test(url);

const UNDERLINE_CLASS = {
  always: css.underlineAlways,
  hover: css.underlineHover,
  none: css.underlineNone,
};

const COLOR_CLASS = {
  primary: css.primary,
  secondary: css.secondary,
  inherit: css.inherit,
};

const Link = React.forwardRef(
  (
    {
      to,
      href,
      external,
      underline = "hover",
      color = "primary",
      className,
      children,
      ...rest
    },
    ref,
  ) => {
    const destination = to ?? href;
    const isExternal = external ?? isExternalUrl(destination ?? "");

    const classes = [
      css.root,
      COLOR_CLASS[color],
      UNDERLINE_CLASS[underline],
      className,
    ]
      .filter(Boolean)
      .join(" ");

    if (isHandoffUrl(destination ?? "")) {
      return (
        <a ref={ref} href={destination} className={classes} {...rest}>
          {children}
        </a>
      );
    }

    if (isExternal) {
      return (
        <a
          ref={ref}
          href={destination}
          className={classes}
          target="_blank"
          rel="noopener noreferrer"
          {...rest}
        >
          {children}
          <ExternalLinkIcon className={css.externalIcon} />
          <span className={shared.visuallyHidden}>(opens in new tab)</span>
        </a>
      );
    }

    if (to === undefined) {
      return (
        <a ref={ref} href={href} className={classes} {...rest}>
          {children}
        </a>
      );
    }

    return (
      <RouterLink ref={ref} to={to} className={classes} {...rest}>
        {children}
      </RouterLink>
    );
  },
);

Link.displayName = "Link";

Link.propTypes = {
  to: PropTypes.string,
  href: PropTypes.string,
  external: PropTypes.bool,
  underline: PropTypes.oneOf(["always", "hover", "none"]),
  color: colorOf("primary", "secondary", "inherit"),
  className: PropTypes.string,
  children: PropTypes.node,
};

export default Link;
